import {
    keepPreviousData,
    type QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/api/types';
import { isRecord, type ListResponse } from '../../lib/api/wire';
import { isWriteOutcomeUnknown } from '../../lib/api/writeOutcome';
import { useRetryHold } from '../../lib/hooks/useRetryHold';
import {
    completeActivity,
    createActivity,
    deleteActivity,
    getActivity,
    getActivityCounts,
    listActivities,
    listActivityTypes,
    listAssigneeCandidates,
    reopenActivity,
    updateActivity,
} from './activitiesApi';
import type {
    ActivitiesListParams,
    ActivityDetail,
    ActivityItem,
    ActivityPatchRequest,
    ActivityRecordRef,
    ActivityScope,
} from './types';

/**
 * The Activities page's server state on TanStack Query (F3-a WI-1.2 plan Decision 9), following
 * `src/features/users/usersQueries.ts`, the pattern every list inherits (R-FND-002).
 */

/**
 * The cache keys, from the broadest down. Every write invalidates `lists()` and `countsAll()`; rows 7–9
 * write their answer to `detail(id)`, and row 10 drops it once nothing reads it (`useDeleteActivity`). The
 * nav badge reads `counts('mine')` (F3-a WI-2.2 plan Decision 5).
 */
export const activitiesKeys = {
    all: ['activities'] as const,
    lists: () => [...activitiesKeys.all, 'list'] as const,
    list: (params: ActivitiesListParams) => [...activitiesKeys.lists(), params] as const,
    countsAll: () => [...activitiesKeys.all, 'counts'] as const,
    /** Row 4's follow-up, read by the edit dialog. */
    detail: (id: number) => [...activitiesKeys.all, 'detail', id] as const,
    counts: (scope: ActivityScope) => [...activitiesKeys.countsAll(), scope] as const,
    types: () => [...activitiesKeys.all, 'types'] as const,
    candidates: (record: ActivityRecordRef) =>
        [...activitiesKeys.all, 'candidates', record.type, record.id] as const,
};

/**
 * What the list's data region renders — the six states of `coding-standards.md` §3.2, the populated one
 * carrying whether its rows are being re-read:
 *
 *  - `loading` — nothing to show yet: the skeleton rows.
 *  - `error` — the read failed with nothing on screen: the danger panel with *Retry*.
 *  - `nothing-yet` / `nothing-matches` — a `200` with `totalItems: 0`, told apart by the request: a
 *    bucket pill or *Completed* chosen (nothing-matches) or neither (nothing-yet). *Mine* and *All I
 *    can see* are scopes, not filters (F3-a spec FR-V6).
 *  - `rows` — `refreshing` while the rows on screen are re-read, `kept` while they are the last good
 *    rows shown as another page or filter loads.
 *  - `stale` — a failed re-read: the last good rows stay, with when they were read and what they answer.
 *
 * `rows` and `stale` carry the params `list` was read for — the snapshot's while it is kept or stale — so
 * the rows are grouped as they were read, never by filters they do not answer.
 */
export type ActivitiesListView =
    | { readonly state: 'loading' }
    | { readonly state: 'error'; readonly error: unknown }
    | { readonly state: 'nothing-yet' }
    | { readonly state: 'nothing-matches' }
    | {
          readonly state: 'rows';
          readonly list: ListResponse<ActivityItem>;
          readonly listParams: ActivitiesListParams;
          readonly refreshing: boolean;
          readonly kept: boolean;
      }
    | {
          readonly state: 'stale';
          readonly list: ListResponse<ActivityItem>;
          readonly listParams: ActivitiesListParams;
          /** Epoch milliseconds of the read that produced `list` — the as-of label. */
          readonly asOf: number;
          readonly error: unknown;
          readonly shows: StaleRowsFor;
      };

/** Whether the rows kept on a failed read answer the same request, another page, or other filters. */
export type StaleRowsFor = 'same' | 'page' | 'filters';

/** A bucket pill or *Completed* is chosen — what makes an empty answer *nothing matches*. */
export function hasActivitiesFilter(params: ActivitiesListParams): boolean {
    return params.view === 'completed' || params.bucket !== null;
}

function staleRowsFor(shown: ActivitiesListParams, current: ActivitiesListParams): StaleRowsFor {
    if (shown.view !== current.view || shown.bucket !== current.bucket || shown.scope !== current.scope) {
        return 'filters';
    }
    return shown.page !== current.page || shown.size !== current.size ? 'page' : 'same';
}

export interface ActivitiesListQuery {
    readonly view: ActivitiesListView;
    /**
     * A page with no rows that is not page 1 (the set shrank or emptied between reads): the page the screen
     * should step back to, else `null`.
     */
    readonly stepBackTo: number | null;
    /**
     * Re-read the current params — the *Retry* of `error` and `stale`. Never rejects: a failure lands in
     * `error` or `stale`. The failure on screen is kept until the read's next result, so the focused *Retry*
     * stays on the page through a retry that fails again (`useRetryHold`, L-183).
     */
    readonly refetch: () => Promise<void>;
    /** A failure is held on screen while its *Retry* re-reads. */
    readonly retrying: boolean;
}

interface Snapshot {
    readonly list: ListResponse<ActivityItem>;
    readonly dataUpdatedAt: number;
    readonly params: ActivitiesListParams;
}

/** A page with no rows that is not page 1 — what the screen steps back from, never a zero state. */
function isOffTheEnd(list: ListResponse<ActivityItem>, params: ActivitiesListParams): boolean {
    return list.items.length === 0 && params.page > 1;
}

/**
 * One page of follow-ups for `params`, mapped onto the six states exactly as `useUsersList` does —
 * `placeholderData: keepPreviousData`, the last-good snapshot for a failed new key, and the step back
 * from a page past the end. The states, first match wins:
 *
 *  1. fetching with no fresh data for the current key, and a snapshot → the snapshot's rows, refreshing;
 *  2. an error with a snapshot → `stale`;
 *  3. an error with no snapshot → `error`;
 *  4. nothing yet and no snapshot → `loading`;
 *  5. then from the fresh data: a page with no rows that is not page 1 → the snapshot's rows or `loading`,
 *     with `stepBackTo` — page 1 when `totalItems` is 0, the last page when `totalPages` is before this
 *     page; a page within `totalPages` that came back empty disagrees with its own totals, so it is re-read
 *     once, and stepped back by one page if it is still empty; `totalItems: 0` → `nothing-matches` with a
 *     filter, else `nothing-yet`; otherwise `rows`, refreshing while a same-key re-read is in flight.
 *
 * Ahead of all of them: a failure held through its *Retry* stays `error` or `stale` until the read answers.
 */
export function useActivitiesList(params: ActivitiesListParams): ActivitiesListQuery {
    const query = useQuery({
        queryKey: activitiesKeys.list(params),
        queryFn: () => listActivities(params),
        placeholderData: keepPreviousData,
    });
    const snapshotRef = useRef<Snapshot | null>(null);
    const { refetch: refetchQuery } = query;
    // The key an empty page within its own `totalPages` has already been re-read for (step 5) — once per key.
    const keyText = JSON.stringify(activitiesKeys.list(params));
    const [reReadFor, setReReadFor] = useState<string | null>(null);

    const fresh = query.isSuccess && !query.isPlaceholderData ? query.data : undefined;
    // Written during render from the query result alone, so a repeated render writes the same value.
    if (fresh !== undefined && !isOffTheEnd(fresh, params)) {
        snapshotRef.current =
            fresh.items.length > 0 ? { list: fresh, dataUpdatedAt: query.dataUpdatedAt, params } : null;
    }
    const snapshot = snapshotRef.current;
    const retryHold = useRetryHold({
        error: query.error,
        isError: query.isError,
        hasData: fresh !== undefined,
        errorUpdateCount: query.errorUpdateCount,
        errorUpdatedAt: query.errorUpdatedAt,
    });

    const refetch = async (): Promise<void> => {
        retryHold.hold();
        await refetchQuery();
    };

    const kept = (shown: Snapshot): ActivitiesListView => ({
        state: 'rows',
        list: shown.list,
        listParams: shown.params,
        refreshing: true,
        kept: true,
    });

    let view: ActivitiesListView;
    let stepBackTo: number | null = null;
    let reRead = false;
    const failed = (error: unknown): ActivitiesListView =>
        snapshot !== null
            ? {
                  state: 'stale',
                  list: snapshot.list,
                  listParams: snapshot.params,
                  asOf: snapshot.dataUpdatedAt,
                  error,
                  shows: staleRowsFor(snapshot.params, params),
              }
            : { state: 'error', error };
    if (retryHold.held !== null) {
        view = failed(retryHold.held.error);
    } else if (query.isFetching && fresh === undefined && snapshot !== null) {
        view = kept(snapshot);
    } else if (query.isError) {
        view = failed(query.error);
    } else if (fresh === undefined) {
        view = snapshot !== null ? kept(snapshot) : { state: 'loading' };
    } else if (isOffTheEnd(fresh, params)) {
        if (fresh.page.totalItems === 0) {
            stepBackTo = 1;
        } else if (fresh.page.totalPages < params.page) {
            stepBackTo = Math.max(1, fresh.page.totalPages);
        } else if (query.isFetching) {
            // The one re-read is in flight.
        } else if (reReadFor === keyText) {
            stepBackTo = params.page - 1;
        } else {
            reRead = true;
        }
        view = snapshot !== null ? kept(snapshot) : { state: 'loading' };
    } else if (fresh.page.totalItems === 0) {
        view = hasActivitiesFilter(params) ? { state: 'nothing-matches' } : { state: 'nothing-yet' };
    } else {
        view = { state: 'rows', list: fresh, listParams: params, refreshing: query.isFetching, kept: false };
    }

    useEffect(() => {
        if (reRead) {
            setReReadFor(keyText);
            void refetchQuery();
        }
    }, [reRead, keyText, refetchQuery]);

    return { view, stepBackTo, refetch, retrying: retryHold.held !== null };
}

/**
 * The open follow-ups per bucket under `scope` (row 3) — the pills' and the headings' numbers, and under
 * *Mine* the nav badge's (`useOverdueFollowUps`, which reads only while `enabled`).
 */
export function useActivityCounts(
    scope: ActivityScope,
    { enabled = true }: { readonly enabled?: boolean } = {},
) {
    return useQuery({
        queryKey: activitiesKeys.counts(scope),
        queryFn: () => getActivityCounts(scope),
        enabled,
    });
}

/** The active follow-up types (row 1), for the dialog's *Type* select. Read only while it is open. */
export function useActivityTypes({ enabled }: { readonly enabled: boolean }) {
    return useQuery({ queryKey: activitiesKeys.types(), queryFn: listActivityTypes, enabled });
}

/**
 * Who may be assigned a follow-up on `record` (row 6, every page of `size=100`, read once per record
 * while it is cached). Read only while the dialog is open
 * and a record is chosen (F3-a WI-1.2 plan Decision 17).
 */
export function useAssigneeCandidates(
    record: ActivityRecordRef | null,
    { enabled }: { readonly enabled: boolean },
) {
    return useQuery({
        queryKey:
            record === null
                ? [...activitiesKeys.all, 'candidates', 'none']
                : activitiesKeys.candidates(record),
        queryFn: () => {
            if (record === null) {
                throw new Error('No record chosen');
            }
            return listAssigneeCandidates(record);
        },
        enabled: enabled && record !== null,
    });
}

/**
 * Create a follow-up (row 5). On `201` every list page and every count is invalidated, so the new
 * follow-up appears under its bucket and the pills and headings count it (F3-a WI-1.2 plan Decision 9).
 * A failure whose outcome is unknown (`isWriteOutcomeUnknown`: a network failure, a `5xx` with no problem
 * body, a `2xx` whose body does not narrow) invalidates the same, so the list behind the dialog shows
 * whether the follow-up landed before a second *Save* (L-191; `contactsQueries.ts`'s `refreshAfterFailure`).
 * A refusal invalidates nothing.
 */
export function useCreateActivity() {
    const queryClient = useQueryClient();
    // Not awaited: the dialog closes on the `201` and the lists re-read behind it.
    const invalidate = () => {
        void queryClient.invalidateQueries({ queryKey: activitiesKeys.lists() });
        void queryClient.invalidateQueries({ queryKey: activitiesKeys.countsAll() });
    };
    return useMutation({
        mutationFn: createActivity,
        onSuccess: invalidate,
        onError: (error) => {
            if (isWriteOutcomeUnknown(error)) {
                invalidate();
            }
        },
    });
}

/**
 * Row 4: one follow-up for the edit dialog, read while it is open (F3-a WI-2.2 plan Decision 12). Every
 * opening reads it again (`refetchOnMount: 'always'`), even over a cached answer: the dialog builds its form
 * only from that read, so a `version` cached from an earlier opening is never sent.
 */
export function useActivity(id: number, { enabled }: { readonly enabled: boolean }) {
    return useQuery({
        queryKey: activitiesKeys.detail(id),
        queryFn: () => getActivity(id),
        enabled,
        refetchOnMount: 'always',
    });
}

/** Re-read every list page and every count — not awaited: the screen re-reads behind the write. */
function invalidateListsAndCounts(queryClient: QueryClient): void {
    void queryClient.invalidateQueries({ queryKey: activitiesKeys.lists() });
    void queryClient.invalidateQueries({ queryKey: activitiesKeys.countsAll() });
}

/** Whether a cached list key was read for the *Open* view (`activitiesKeys.list(params)`). */
function isOpenListKey(key: readonly unknown[]): boolean {
    const params = key[2];
    return isRecord(params) && params.view === 'open';
}

/**
 * Take a completed follow-up out of every cached *Open* page at once, so its row leaves the list before
 * the re-read answers (F3-a spec FR-V3). The page's `totalItems` drops with it, so the page stays
 * consistent with its own rows; the re-read then brings the server's numbers.
 */
function removeFromOpenLists(queryClient: QueryClient, id: number): void {
    for (const [key, list] of queryClient.getQueriesData<ListResponse<ActivityItem>>({
        queryKey: activitiesKeys.lists(),
    })) {
        if (list === undefined || !isOpenListKey(key) || !list.items.some((item) => item.id === id)) {
            continue;
        }
        queryClient.setQueryData<ListResponse<ActivityItem>>(key, {
            items: list.items.filter((item) => item.id !== id),
            page: { ...list.page, totalItems: Math.max(0, list.page.totalItems - 1) },
        });
    }
}

/**
 * The one after-write step every follow-up write shares (F3-a WI-2.2 plan Decision 5):
 *
 *  - `saved` — a `200` answer goes to `detail(id)`, and every list page and count is re-read, the nav badge
 *    included (it reads `counts('mine')`);
 *  - `failed` — an outcome that is unknown (`isWriteOutcomeUnknown`, L-191) re-reads the same, so the
 *    screen shows what landed before a second try; a `404 NOT_FOUND` too, because the follow-up is gone or
 *    out of reach; any other refusal re-reads nothing.
 */
function useAfterWrite() {
    const queryClient = useQueryClient();
    return {
        queryClient,
        saved(detail: ActivityDetail): void {
            queryClient.setQueryData(activitiesKeys.detail(detail.id), detail);
            invalidateListsAndCounts(queryClient);
        },
        failed(error: unknown): void {
            if (isWriteOutcomeUnknown(error) || (error instanceof ApiError && error.code === 'NOT_FOUND')) {
                invalidateListsAndCounts(queryClient);
            }
        },
    };
}

/** Which follow-up a write is for, and the `version` last read for it. */
export interface ActivityVersionRef {
    readonly id: number;
    readonly version: number;
}

/** Row 7: save the edit dialog's changes — the request `toPatchRequest` built (F3-a WI-2.2 plan Decision 3). */
export function useUpdateActivity() {
    const after = useAfterWrite();
    return useMutation({
        mutationFn: ({ id, request }: { readonly id: number; readonly request: ActivityPatchRequest }) =>
            updateActivity(id, request),
        onSuccess: after.saved,
        onError: after.failed,
    });
}

/**
 * Row 8: *Mark done*. Besides the shared step, the follow-up leaves every cached *Open* page at once
 * (F3-a WI-2.2 plan Decision 5).
 */
export function useCompleteActivity() {
    const after = useAfterWrite();
    return useMutation({
        mutationFn: ({ id, version }: ActivityVersionRef) => completeActivity(id, version),
        onSuccess: (detail) => {
            removeFromOpenLists(after.queryClient, detail.id);
            after.saved(detail);
        },
        onError: after.failed,
    });
}

/** Row 9: *Undo* after *Mark done*, and the dialog's *Reopen*. */
export function useReopenActivity() {
    const after = useAfterWrite();
    return useMutation({
        mutationFn: ({ id, version }: ActivityVersionRef) => reopenActivity(id, version),
        onSuccess: after.saved,
        onError: after.failed,
    });
}

/**
 * Row 10: *Delete*. On the `204` the lists and counts re-read and the cached follow-up is dropped. While
 * the edit dialog still reads it — it closes in the same turn — the entry is left to be collected once the
 * dialog has gone: dropping an entry something still reads would make it read the deleted follow-up
 * again, answered `404`, and unmount the dialog before its own `204` steps ran.
 */
export function useDeleteActivity() {
    const after = useAfterWrite();
    return useMutation({
        mutationFn: ({ id, version }: ActivityVersionRef) => deleteActivity(id, version),
        onSuccess: (_answer, { id }) => {
            invalidateListsAndCounts(after.queryClient);
            const cached = after.queryClient
                .getQueryCache()
                .find({ queryKey: activitiesKeys.detail(id), exact: true });
            if (cached !== undefined && cached.getObserversCount() === 0) {
                after.queryClient.removeQueries({ queryKey: activitiesKeys.detail(id), exact: true });
            }
        },
        onError: after.failed,
    });
}
